import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const FOCUSABLE = 'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])';

// Bottom sheet on phones, centered dialog on desktop.
// Closes on Escape, scrim tap, close button, or swiping down from the top of the sheet.
export default function Sheet({ open, onClose, title, subtitle, children, labelledBy = 'hx-sheet-title', portalTarget }) {
    const [closing, setClosing] = useState(false);
    const sheetRef = useRef(null);
    const lastFocus = useRef(null);
    const drag = useRef(null);

    const close = useCallback(() => {
        setClosing(true);
        setTimeout(() => {
            setClosing(false);
            onClose();
        }, 200);
    }, [onClose]);

    useEffect(() => {
        if (!open) return undefined;
        lastFocus.current = document.activeElement;
        const el = sheetRef.current;
        const first = el?.querySelector(FOCUSABLE);
        (first || el)?.focus({ preventScroll: true });

        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                close();
            } else if (e.key === 'Tab' && el) {
                const items = [...el.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null);
                if (!items.length) return;
                const firstItem = items[0];
                const lastItem = items[items.length - 1];
                if (e.shiftKey && document.activeElement === firstItem) {
                    e.preventDefault();
                    lastItem.focus();
                } else if (!e.shiftKey && document.activeElement === lastItem) {
                    e.preventDefault();
                    firstItem.focus();
                }
            }
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
            lastFocus.current?.focus?.({ preventScroll: true });
        };
    }, [open, close]);

    if (!open) return null;

    const onTouchStart = (e) => {
        if (sheetRef.current.scrollTop > 0) return;
        drag.current = { y: e.touches[0].clientY, dy: 0 };
    };
    const onTouchMove = (e) => {
        if (!drag.current) return;
        const dy = Math.max(0, e.touches[0].clientY - drag.current.y);
        drag.current.dy = dy;
        sheetRef.current.style.transform = `translateY(${dy}px)`;
    };
    const onTouchEnd = () => {
        if (!drag.current) return;
        const { dy } = drag.current;
        drag.current = null;
        sheetRef.current.style.transform = '';
        if (dy > 90) close();
    };

    const node = (
        <div className="hx-sheet-root">
            <div className="hx-sheet-scrim" onClick={close} aria-hidden="true" />
            <div
                ref={sheetRef}
                className="hx-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby={labelledBy}
                tabIndex={-1}
                data-closing={closing}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                <div className="hx-sheet-handle" aria-hidden="true" />
                <button type="button" className="hx-sheet-close" onClick={close} aria-label="Close">
                    <X />
                </button>
                {title && (
                    <h2 id={labelledBy} className="hx-sheet-title">
                        {title}
                    </h2>
                )}
                {subtitle && <p className="hx-sheet-sub">{subtitle}</p>}
                {typeof children === 'function' ? children(close) : children}
            </div>
        </div>
    );

    // Render inside the themed card root so sheets inherit theme tokens.
    return portalTarget ? createPortal(node, portalTarget) : node;
}
